'use client'

import { useState } from 'react'

import { EllipsisVertical, PlusIcon } from 'lucide-react'
import { useForm, useWatch } from 'react-hook-form'

import { Card } from '~/components/Card'
import { PaymentInfoDialog } from '~/components/compliance/PaymentInfoDialog'
import {
  AddressSearchInput,
  Button,
  CheckboxGroupInput,
  CurrencyAmount,
  Group,
  List,
  Message,
  NetworkDisplay,
  SelectInput,
  Skeleton,
  TextInput,
  Tooltip,
} from '~/components/component-library'
import { AddressDisplay } from '~/components/component-library/AddressDisplay'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { Dropdown } from '~/components/component-library/Dropdown'
import { Timestamp } from '~/components/component-library/Timestamp'
import { buildSendQueryParams } from '~/components/forms/transfer/SendButton'
import { useDefaultRecipientsFormatted } from '~/components/forms/transfer/dialog/DialogWrapper'
import { SupportedChainId } from '~/constants/chains'
import { useAuth } from '~/context/AuthProvider'
import {
  useInvitePayee,
  useResendPayeeInvitation,
  useRevokePayeeInvitation,
} from '~/hooks/compliance'
import {
  useOrgCompliance,
  usePayee1099,
  usePayee1099Years,
  usePayeeTaxCertificate,
} from '~/hooks/compliance'
import { usePayeeLookupById } from '~/hooks/compliance/usePayeeLookup'
import { useActiveOrgMember } from '~/hooks/org'
import { usePlanAccess } from '~/hooks/usePlanAccess'
import { useAllChainIds } from '~/hooks/utils'
import { CompliantPaymentRelation } from '~/types'
import { isValidEmail } from '~/utils/authUser'
import { displayCurrency } from '~/utils/display'

import type {
  DropdownEntry,
  DropdownItem,
} from '~/components/component-library/Dropdown'

export const PayeeList = () => {
  const { setGlobalModalState, closeGlobalModal } = useAuth()
  const { payees } = useOrgCompliance()
  const { canAccess: canInvite } = usePlanAccess('premium')

  return (
    <Card>
      <div className="flex items-center justify-between">
        <h3 className="text-lg">Recipients</h3>
        <Button
          prefix={<PlusIcon />}
          size="small"
          isDisabled={!canInvite}
          disabledReason="Inviting recipients requires Splits Premium"
          eventName="clickedInviteRecipientCompliance"
          onClick={() => {
            setGlobalModalState({
              isOpen: true,
              content: <InvitePayeeDialog onCloseAction={closeGlobalModal} />,
            })
          }}
        >
          Invite
        </Button>
      </div>
      <p className="text-sm">
        Invite recipients, make payments, and retrieve tax forms
      </p>
      <List
        items={[
          ...payees.map((payee) => {
            return {
              key: payee.id,
              content: payee.address ? (
                <AddressDisplay address={payee.address} layout="standard" />
              ) : (
                <>Missing payment info...</>
              ),
              description: payee.payeeInvitedEmail,
              meta: <PayeeStatus payee={payee} />,
              actionIcon: <PayeeActions payee={payee} />,
            }
          }),
        ]}
      />
    </Card>
  )
}

const PayeeStatus = ({ payee }: { payee: CompliantPaymentRelation }) => {
  const { isVerified, isLoading, hasAcceptedInvitation, verifiedAt } =
    usePayeeLookupById(payee.id)

  return (
    <div className="flex items-center space-x-2 text-xs">
      {isLoading ? (
        <Skeleton size="sm" length={4} />
      ) : isVerified && verifiedAt ? (
        payee.usdTotal ? (
          <Tooltip content={`Total USD paid in this filing year`}>
            <CurrencyAmount value={payee.usdTotal} layout="inline" />
          </Tooltip>
        ) : (
          <Tooltip
            content={
              payee.address
                ? 'Ready to receive payments'
                : 'Missing payment info'
            }
          >
            <span>
              Verified <Timestamp timestamp={verifiedAt} type="absolute" />
            </span>
          </Tooltip>
        )
      ) : (
        <Tooltip
          content={
            hasAcceptedInvitation
              ? 'Invitation accepted, verification incomplete'
              : 'Invitation created'
          }
        >
          <>{hasAcceptedInvitation ? 'Onboarding' : 'Invited'}</>
        </Tooltip>
      )}
    </div>
  )
}

const PayeeActions = ({ payee }: { payee: CompliantPaymentRelation }) => {
  const { isVerified, hasAcceptedInvitation } = usePayeeLookupById(payee.id)
  if (isVerified) return <VerifiedPayeeDropdown payee={payee} />
  if (hasAcceptedInvitation) return <OnboardingPayeeDropdown payee={payee} />
  return <InvitedPayeeDropdown payee={payee} />
}

const OnboardingPayeeDropdown = ({
  payee,
}: {
  payee: CompliantPaymentRelation
}) => {
  const editPaymentInfoItem = usePayeeEditPaymentInfoItem(payee)
  return (
    <Dropdown items={[editPaymentInfoItem]}>
      <button type="button" aria-label="Recipient actions">
        <EllipsisVertical size={16} />
      </button>
    </Dropdown>
  )
}

const InvitedPayeeDropdown = ({
  payee,
}: {
  payee: CompliantPaymentRelation
}) => {
  const items = usePayeeInvitedItems(payee)
  return (
    <Dropdown items={items}>
      <button type="button" aria-label="Recipient actions">
        <EllipsisVertical size={16} />
      </button>
    </Dropdown>
  )
}

const VerifiedPayeeDropdown = ({
  payee,
}: {
  payee: CompliantPaymentRelation
}) => {
  const items = usePayeeVerifiedItems(payee)
  return (
    <Dropdown items={items}>
      <button type="button" aria-label="Recipient actions">
        <EllipsisVertical size={16} />
      </button>
    </Dropdown>
  )
}

const usePayeeVerifiedItems = (
  payee: CompliantPaymentRelation,
): DropdownEntry[] => {
  const { setGlobalModalState, closeGlobalModal } = useAuth()
  const activeMemberRole = useActiveOrgMember()?.role
  const isOwner = activeMemberRole === 'OWNER'

  const { isUSBased, isIndividual } = usePayeeLookupById(payee.id)

  const isEligibleFor1099 = isUSBased

  const defaultRecipientsFormatted = useDefaultRecipientsFormatted(
    payee.address,
  )

  const editPaymentInfoItem = usePayeeEditPaymentInfoItem(payee)
  const taxCertificateItem = useTaxCertificateItem(
    payee,
    !!isUSBased,
    !!isIndividual,
  )

  const items: DropdownEntry[] = []
  if (payee.address) {
    items.push({
      content: 'Pay...',
      href: {
        path: `/send${buildSendQueryParams({ defaultRecipients: defaultRecipientsFormatted })}`,
      },
    })
  }
  items.push(editPaymentInfoItem)
  if (isOwner) items.push(taxCertificateItem)
  if (isOwner && isEligibleFor1099) {
    items.push({
      content: 'View Form 1099-NEC',
      onClick: () =>
        setGlobalModalState({
          isOpen: true,
          content: (
            <Preview1099Dialog onClose={closeGlobalModal} payee={payee} />
          ),
        }),
    })
  }
  return items
}

const usePayeeEditPaymentInfoItem = (
  payee: CompliantPaymentRelation,
): DropdownItem => {
  const { setGlobalModalState, closeGlobalModal } = useAuth()
  const activeMemberRole = useActiveOrgMember()?.role
  const isOwner = activeMemberRole === 'OWNER'
  const hasPaymentInfo =
    !!payee.address || (payee.chains && payee.chains.length > 0)

  return {
    content: `${hasPaymentInfo ? 'Edit' : 'Add'} payment info...`,
    disabled: !isOwner,
    tooltip: !isOwner ? 'Only owners can edit payment info' : undefined,
    onClick: () =>
      setGlobalModalState({
        isOpen: true,
        content: <PaymentInfoDialog onClose={closeGlobalModal} payee={payee} />,
      }),
  }
}

const usePayeeInvitedItems = (
  payee: CompliantPaymentRelation,
): DropdownEntry[] => {
  const { setGlobalModalState, closeGlobalModal } = useAuth()

  return [
    {
      content: 'Resend...',
      onClick: () =>
        setGlobalModalState({
          isOpen: true,
          content: (
            <ResendInvitationDialog payee={payee} onClose={closeGlobalModal} />
          ),
        }),
    },
    {
      content: 'Revoke...',
      onClick: () =>
        setGlobalModalState({
          isOpen: true,
          content: (
            <RevokeInvitationDialog payee={payee} onClose={closeGlobalModal} />
          ),
        }),
    },
  ]
}

const useTaxCertificateItem = (
  payee: CompliantPaymentRelation,
  isUSBased: boolean,
  isIndividual: boolean,
): DropdownItem => {
  const { downloadDocument } = usePayeeTaxCertificate(payee.id)
  const isForeignEntity = !isUSBased && !isIndividual

  return {
    content: `View Form ${isUSBased ? 'W-9' : isIndividual ? 'W-8BEN' : 'W-8BEN-E'}`,
    disabled: isForeignEntity,
    tooltip: isForeignEntity
      ? 'Form W-8BEN-E download not supported yet, please email support@splits.org'
      : undefined,
    onClick: downloadDocument,
  }
}

const Preview1099Dialog = ({
  onClose,
  payee,
}: {
  onClose: () => void
  payee: CompliantPaymentRelation
}) => {
  const { data, isLoading: isLoadingYears } = usePayee1099Years(payee.id)
  const years = data?.years ?? []
  const [selectedYear, setSelectedYear] = useState<number>()
  const filingYear = selectedYear ?? years[0]?.filingYear
  const filingYearData = years.find((year) => year.filingYear === filingYear)
  const filingThreshold = filingYear && filingYear >= 2026 ? '$2,000' : '$600'
  const { downloadDocument, isDownloading, enabled } = usePayee1099(
    payee.id,
    filingYear ?? 0,
  )

  return (
    <SplitsDialogContent
      id="preview-1099-dialog"
      title={`Form 1099-NEC for ${payee.payeeOrg?.name}`}
      variant="cta"
      isLoading={isDownloading || isLoadingYears}
      actionButton={{
        text: 'Download 1099-NEC',
        onClick: downloadDocument,
        isDisabled: !enabled || !filingYear,
      }}
      cancelButton={{ text: 'Cancel', onClick: onClose }}
    >
      <div className="flex flex-col gap-4">
        {years.length === 0 && !isLoadingYears ? (
          <Message variant="info">
            No 1099-NEC forms are available because this recipient has no
            successful payments in a supported tax year.
          </Message>
        ) : (
          <>
            <SelectInput
              label="Tax year"
              placeholder="Select a tax year"
              options={years.map(({ filingYear: year }) => ({
                value: year,
                label: String(year),
              }))}
              selectedOption={filingYear}
              selectValue={setSelectedYear}
            />
            {filingYearData && (
              <Message variant="info">
                {`This recipient received ${displayCurrency(Number(filingYearData.usdTotal))} in payments for tax year ${filingYear}. The filing threshold for ${filingYear} is ${filingThreshold}. Forms for the current year are previews until year-end.`}
              </Message>
            )}
          </>
        )}
      </div>
    </SplitsDialogContent>
  )
}

const ResendInvitationDialog = ({
  payee,
  onClose,
}: {
  payee: CompliantPaymentRelation
  onClose: () => void
}) => {
  const { resendPayeeInvitation, isProcessing: isResendingInvitation } =
    useResendPayeeInvitation()

  const onResendInvitation = () => {
    if (isResendingInvitation) return
    resendPayeeInvitation(payee.id, onClose)
  }
  return (
    <SplitsDialogContent
      id="resend-invitation-dialog"
      title="Resend invitation"
      variant="cta"
      isLoading={isResendingInvitation}
      actionButton={{
        text: 'Resend',
        onClick: onResendInvitation,
      }}
      cancelButton={{ text: 'Cancel', onClick: onClose }}
    >
      <div>
        A new invitation email will be sent to {payee.payeeInvitedEmail}, and
        any existing invitation emails will become invalid.
      </div>
    </SplitsDialogContent>
  )
}

const RevokeInvitationDialog = ({
  onClose,
  payee,
}: {
  onClose: () => void
  payee: CompliantPaymentRelation
}) => {
  const { revokePayeeInvitation, isProcessing: isRevokingInvitation } =
    useRevokePayeeInvitation()

  const onRevokeInvitation = () => {
    if (isRevokingInvitation) return
    revokePayeeInvitation(payee.id!, onClose)
  }

  return (
    <SplitsDialogContent
      id="revoke-invitation-dialog"
      title="Revoke invitation"
      variant="cta"
      isLoading={isRevokingInvitation}
      actionButton={{
        text: 'Revoke',
        variant: 'danger',
        onClick: onRevokeInvitation,
      }}
      cancelButton={{ text: 'Cancel', onClick: onClose }}
    >
      Any existing invitation emails for {payee.payeeInvitedEmail} will become
      invalid.
    </SplitsDialogContent>
  )
}

export type InvitePayeeFormValues = {
  email: string
  address?: string
  chainIds: SupportedChainId[]
}

export const InvitePayeeDialog = ({
  onCloseAction,
}: {
  onCloseAction: () => void
}) => {
  const { control, handleSubmit, setValue } = useForm<InvitePayeeFormValues>({
    defaultValues: {
      email: '',
      address: undefined,
      chainIds: [],
    },
  })

  const { invitePayee, isProcessing } = useInvitePayee()
  const email = useWatch({ control, name: 'email' })

  const onSubmit = async (formData: InvitePayeeFormValues) => {
    invitePayee(formData, onCloseAction)
  }

  const address = useWatch({ control, name: 'address' })
  const chainIds = useWatch({ control, name: 'chainIds' })
  const allChainIds = useAllChainIds()

  // If either address or chainIds is set, we need to validate the address
  const isSubmitDisabled =
    !isValidEmail(email) || (!!address && chainIds.length === 0)

  return (
    <SplitsDialogContent
      id="invite-payee-dialog"
      title="Invite recipient"
      variant="cta"
      isLoading={isProcessing}
      actionButton={{
        text: 'Submit',
        onClick: handleSubmit(onSubmit),
        isDisabled: isSubmitDisabled,
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>The new recipient will be sent an email to onboard</div>
        <TextInput
          label="Email"
          control={control}
          inputName={'email'}
          placeholder={'name@example.com'}
          validationFunc={(text) => {
            if (!isValidEmail(text)) return 'Invalid email address'
            return true
          }}
          autoFocus={true}
          inputType="email"
          autoComplete={'email'}
        />
        <Group
          label="Address"
          secondaryLabel="Optional"
          tooltip="You can enter the recipient's address now, or later"
        >
          <AddressSearchInput
            control={control}
            inputName="address"
            setValue={setValue}
            optional
          />
        </Group>
        {address && (
          <CheckboxGroupInput
            label="Chains"
            tooltip="Select the chains you want to pay the recipient on"
            control={control}
            inputName="chainIds"
            options={allChainIds.map((chainId) => ({
              value: chainId,
              display: () => (
                <NetworkDisplay layout="standard" chainId={chainId} />
              ),
            }))}
          />
        )}
      </form>
    </SplitsDialogContent>
  )
}
